import React from "react";
import styled from "styled-components";
import { InboxIcon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { colors } from "src/theme/colors";
import { line, radii } from "src/theme/ui";

/**
 * EmptyState — football's "nothing here yet" treatment for a card interior,
 * ported. Use it wherever a chart, list or table would otherwise render blank
 * space: a quiet icon, a plain one-line title, and an optional hint that says
 * when the card will fill in.
 */

const Wrap = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 24px 0;
  text-align: center;
`;

const Glyph = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid ${line.hairline};
  border-radius: ${radii.menu}px;
  background: ${colors.gray[50]};
  color: ${colors.gray[500]};
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.gray[600]};
`;

const Hint = styled.div`
  max-width: 240px;
  font-size: 12px;
  line-height: 1.4;
  color: ${colors.gray[500]};
`;

export interface EmptyStateProps {
  /** A hugeicons glyph. Defaults to an inbox. */
  icon?: Parameters<typeof HugeIcon>[0]["icon"];
  title: string;
  hint?: string;
  className?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon = InboxIcon,
  title,
  hint,
  className,
}) => (
  <Wrap className={className}>
    <Glyph>
      <HugeIcon icon={icon} size={17} strokeWidth={1.8} />
    </Glyph>
    <Title>{title}</Title>
    {hint && <Hint>{hint}</Hint>}
  </Wrap>
);

export default EmptyState;
